// What a browser is allowed to say about the date poll. P5-3 T6.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 THIS AND `trips/utils/models.py` ARE TWO IMPLEMENTATIONS OF ONE CONTRACT
// ═══════════════════════════════════════════════════════════════════════════
//
// CLAUDE.md gotcha 21, which has now bitten twice: *"when a guard and a
// consumer both interpret the same key, they are two implementations of one
// contract. Assert them AGAINST EACH OTHER ... never assert each separately
// and infer that they agree."*
//
// So they are not asserted separately. `trips/tests/range_poll_vectors.json` is
// a generated table of bodies with the SERVICE's verdict on each, produced by
// the service's own models; `src/test/functions/range-poll.test.js` drives this
// file over the same table and asserts the same verdicts. A rule that exists on
// one side only fails on the other.
//
// ⚠️ It matters here more than it looks, because the two sides fail
// DIFFERENTLY. The service answers a bad body with FastAPI's 422 and a
// structured detail; `functions/_lib/trips.js` never echoes an upstream body
// (deliberately — see its header) and turns 422 into *"Something in that was
// not valid — check the amount and the currency."* On a date poll that sentence
// is not merely unhelpful, it is about the wrong screen. Every refusal a member
// can trigger therefore has to be caught HERE, in words about dates.
//
// 🔴 UNKNOWN KEYS ARE REFUSED, WHICH `entry-body.js` DOES NOT DO. Its closed
// field list silently ignores extras, and that is safe there because the extras
// never reach the service. It cannot stay silent here: the service's models are
// `extra="forbid"`, so the two sides would disagree on exactly the vectors the
// shared table exists to compare. Refusing is also the better answer — a client
// sending `id` inside a range is trying to name one, and ids are assigned by
// the service precisely so a client cannot.

/** TR8's cap. The service holds the same number twice — once as the refusal
 * a member reads, once as a database CHECK — and this is the third, in front
 * of both, because it is the only one that can answer in a sentence about
 * dates. The vectors are what keep all three the same number. */
export const MAX_CANDIDATES = 10

/** An abuse backstop on the answer, matching the service's `MAX_SELECTABLE`. */
const MAX_SELECTED = 100

const RANGE_KEYS = new Set(['start', 'end', 'tag'])
const PROPOSE_KEYS = new Set(['candidate_ranges', 'deadline'])
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/

/** A calendar date, or null.
 *
 * 🔴 The shape is checked BEFORE `Date.parse`, and then the parse is checked
 * too. `Date.parse('2027-02-30')` is not NaN in every engine — it rolls over —
 * so a shape test alone accepts a day that does not exist and a parse test
 * alone accepts `'March 2027'`. Both, and then a round-trip comparison, is what
 * makes this agree with Python's `date.fromisoformat`.
 */
function isoDate(value) {
  if (typeof value !== 'string' || !ISO_DATE.test(value)) return null
  const parsed = new Date(`${value}T00:00:00Z`)
  if (Number.isNaN(parsed.getTime())) return null
  return parsed.toISOString().slice(0, 10) === value ? value : null
}

/** One candidate range, or an Error sentence. */
function candidate(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return { error: 'One of those dates was not readable.' }
  }
  for (const key of Object.keys(raw)) {
    if (!RANGE_KEYS.has(key)) {
      // Named, because the realistic cause is a client trying to send `id` and
      // watching it never take effect.
      return { error: `A date range has a start, an end and an optional label — not "${key}".` }
    }
  }

  const start = isoDate(raw.start)
  const end = isoDate(raw.end)
  if (!start || !end) return { error: 'A date range needs a start and an end, like 2027-04-10.' }
  if (end <= start) {
    // String comparison is safe on ISO dates and is the same order Python's
    // `date` comparison gives. Equal is refused as well as backwards: TR1 puts
    // single-occasion things in Discord, so a range is at least one night.
    return { error: 'A range has to end after it starts — these are overnight trips.' }
  }

  const value = { start, end }
  if (raw.tag !== undefined && raw.tag !== null) {
    if (typeof raw.tag !== 'string') return { error: 'A label is text, like "school holidays".' }
    const tag = raw.tag.trim()
    if (tag.length > 60) return { error: 'A label has to be shorter than that.' }
    if (tag !== '') value.tag = tag
  }
  return { value }
}

/**
 * The body for PUT /api/trips/:tripId/range-poll.
 *
 * 🔴 THE ELEVENTH RANGE IS REFUSED IN A SENTENCE. The brief for this slice:
 * *"Refuse the 11th plainly — and note the DB already enforces 10, so decide
 * where the honest error lives and make sure the member sees a sentence, not a
 * constraint violation."* This is where a member's copy of that lives; the
 * service refuses it too, and the database CHECK is underneath both.
 */
export function proposeBody(body) {
  if (!body) return { error: 'That request had no body the site could read.' }
  for (const key of Object.keys(body)) {
    if (!PROPOSE_KEYS.has(key)) return { error: 'That request did not make sense.' }
  }

  const rows = body.candidate_ranges
  if (!Array.isArray(rows)) return { error: 'A date poll needs a list of ranges.' }
  if (rows.length === 0) return { error: 'A date poll needs at least one range.' }
  if (rows.length > MAX_CANDIDATES) {
    return {
      error:
        `A date poll can have at most ${MAX_CANDIDATES} ranges, and that is ${rows.length}. ` +
        `Ten is the cap — more than that and people stop answering.`,
    }
  }

  const candidate_ranges = []
  const seen = new Set()
  for (const row of rows) {
    const result = candidate(row)
    if (result.error) return { error: result.error }
    const key = `${result.value.start}|${result.value.end}|${result.value.tag ?? ''}`
    if (seen.has(key)) {
      return { error: 'Two of those ranges are the same dates — each one can only appear once.' }
    }
    seen.add(key)
    candidate_ranges.push(result.value)
  }

  const payload = { candidate_ranges }

  if (body.deadline !== undefined && body.deadline !== null) {
    if (typeof body.deadline !== 'string' || Number.isNaN(Date.parse(body.deadline))) {
      return { error: 'A closing date has to be a date, like 2027-03-28.' }
    }
    payload.deadline = body.deadline
  }

  return { payload }
}

/**
 * The body for PUT /api/trips/:tripId/range-poll/selection.
 *
 * 🔴 `[]` IS A VALID BODY AND IT IS NOT AN EMPTY REQUEST. HANDOFF puts *"None
 * of these work for me"* in the sticky footer specifically so it stays
 * reachable at ten ranges, and calls it *"a real answer, not a non-answer"*.
 * A validator that refused an empty list — the reflex — would delete the one
 * answer that tells the organiser to propose different weeks.
 */
export function selectionBody(body) {
  if (!body) return { error: 'That request had no body the site could read.' }
  for (const key of Object.keys(body)) {
    if (key !== 'range_ids') return { error: 'That request did not make sense.' }
  }

  const ids = body.range_ids
  if (!Array.isArray(ids)) return { error: 'An answer is a list of the dates that work for you.' }
  if (ids.length > MAX_SELECTED) return { error: 'That is more dates than any poll has.' }
  for (const id of ids) {
    if (typeof id !== 'string' || !UUID.test(id)) {
      return { error: 'One of those dates is not one this poll offered. Reload and try again.' }
    }
  }
  // Not lower-cased here: the SERVICE canonicalises, via `uuid.UUID` -> `str`.
  // Two normalisations of one key is how the two sides start disagreeing about
  // what an id is, which is the gotcha this file's header is about.
  return { payload: { range_ids: ids } }
}

/** The body for POST /api/trips/:tripId/range-poll/confirm. */
export function confirmBody(body) {
  if (!body) return { error: 'That request had no body the site could read.' }
  for (const key of Object.keys(body)) {
    if (key !== 'range_id') return { error: 'That request did not make sense.' }
  }
  if (typeof body.range_id !== 'string' || !UUID.test(body.range_id)) {
    return { error: 'Pick one of the ranges on this poll.' }
  }
  return { payload: { range_id: body.range_id } }
}
